import type {
  AskSseEvent,
  CompareResponse,
  Corpus,
  CreateBriefShareRequest,
  CreateBriefShareResponse,
  Dossier,
  GraphData,
  GraphGroupingConfig,
  GraphHealth,
  HubIslandsResponse,
  IngestDiffStats,
  IngestErrorCode,
  IngestRun,
  IngestSseEvent,
  MapEntitiesResponse,
  RollbackIngestResponse,
  SourceFormat,
  StartIngestRequest,
  StatsResponse,
  SurfaceState,
  ThreadSubject,
  ValidateIngestResponse,
} from '@jam/shared';
import {
  CreateBriefShareResponse as CreateBriefShareResponseSchema,
  GraphGroupingConfig as GraphGroupingConfigSchema,
  GraphHealth as GraphHealthSchema,
  IngestRun as IngestRunSchema,
  IngestSseEvent as IngestSseEventSchema,
  ListIngestRunsResponse as ListIngestRunsResponseSchema,
  PreviewDiffResponse as PreviewDiffResponseSchema,
  RollbackIngestResponse as RollbackIngestResponseSchema,
  ValidateIngestResponse as ValidateIngestResponseSchema,
} from '@jam/shared';
import { authHeaders } from './auth-headers';
import { logger } from './logger';

const API_BASE = '/api';

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { headers: await authHeaders() });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    const message = `${res.status} ${body}`;
    logger.warn('api.get failed', {
      path,
      status: res.status,
      body: body.slice(0, 200),
    });
    throw new Error(message);
  }
  return (await res.json()) as T;
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
  return (await res.json()) as T;
}

export const api = {
  stats: () => getJson<StatsResponse>('/stats'),
  graph: (limit = 500) => getJson<GraphData>(`/graph?limit=${limit}`),
  hubs: (count?: number) =>
    getJson<HubIslandsResponse>(
      count ? `/graph/hubs?count=${count}` : '/graph/hubs',
    ),
  subgraph: (entity: string, hops = 2) =>
    getJson<GraphData>(`/subgraph?entity=${encodeURIComponent(entity)}&hops=${hops}`),
  entity: (name: string) => getJson<Dossier>(`/entity/${encodeURIComponent(name)}`),
  compare: (a: string, b: string) =>
    getJson<CompareResponse>(
      `/compare?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}`,
    ),
  mapEntities: () =>
    getJson<MapEntitiesResponse>('/map/entities'),
  cypher: (query: string) =>
    postJson<{ rows: Array<Record<string, unknown>>; count: number }>('/cypher', { query }),
  /**
   * Fetch the current graph-grouping config. Reader-visible. Zod-parsed so
   * a server schema drift surfaces as a warning and a rejected promise
   * rather than a silent mis-render in the admin form.
   */
  async getGraphConfig(): Promise<GraphGroupingConfig> {
    const raw = await getJson<unknown>('/admin/config/graph');
    const parsed = GraphGroupingConfigSchema.safeParse(raw);
    if (!parsed.success) {
      logger.warn('getGraphConfig response failed schema', {
        error: parsed.error.format(),
      });
      throw new Error('graph_config_invalid_response');
    }
    return parsed.data;
  },
  /**
   * Overwrite the graph-grouping config. Admin-only on the server —
   * non-admins will 401/403. Returns the written doc so the caller
   * can refresh its form state + the "last updated" stamp.
   */
  async updateGraphConfig(
    input: Pick<
      GraphGroupingConfig,
      'hubCount' | 'neighborhoodDepth' | 'hubSelection' | 'curatedHubs'
    >,
  ): Promise<GraphGroupingConfig> {
    const res = await fetch(`${API_BASE}/admin/config/graph`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
      body: JSON.stringify(input),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      logger.warn('updateGraphConfig failed', {
        status: res.status,
        body: text.slice(0, 200),
      });
      throw new Error(`${res.status} ${text}`);
    }
    const raw = (await res.json()) as unknown;
    const parsed = GraphGroupingConfigSchema.safeParse(raw);
    if (!parsed.success) {
      logger.warn('updateGraphConfig response failed schema', {
        error: parsed.error.format(),
      });
      throw new Error('graph_config_invalid_response');
    }
    return parsed.data;
  },
  /**
   * Probe Neo4j for ops-surface health. Admin-only on the server; call
   * sites should render a permission-denied hint instead of throwing a
   * raw 401 when the viewer lacks the role.
   */
  async getGraphHealth(): Promise<GraphHealth> {
    const raw = await getJson<unknown>('/admin/graph-health');
    const parsed = GraphHealthSchema.safeParse(raw);
    if (!parsed.success) {
      logger.warn('getGraphHealth response failed schema', {
        error: parsed.error.format(),
      });
      throw new Error('graph_health_invalid_response');
    }
    return parsed.data;
  },
  /** List scheduled (cron) jobs with last-run summary. SuperAdmin-only. */
  getSchedules: () =>
    getJson<import('@jam/shared').ScheduledJobsListResponse>('/admin/schedules'),
  /** Manually trigger one of the scheduled jobs. Server gates path to
   *  the registered superAdmin endpoints; the caller is responsible for
   *  not constructing arbitrary paths. */
  triggerSchedule: (manualTriggerPath: string) =>
    postJson<unknown>(manualTriggerPath, {}),
};

/**
 * Mint a new share token for a saved brief. Analyst+, owner-only on the
 * server. Returns the canonical public URL (the server is the source of
 * truth for the URL shape — don't rebuild it client-side).
 */
export async function createBriefShare(
  briefId: string,
  body: CreateBriefShareRequest,
): Promise<CreateBriefShareResponse> {
  const res = await fetch(
    `${API_BASE}/briefs/${encodeURIComponent(briefId)}/share`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await authHeaders()),
      },
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    logger.warn('createBriefShare failed', {
      briefId,
      status: res.status,
      body: text.slice(0, 200),
    });
    throw new Error(`${res.status} ${text}`);
  }
  const raw = (await res.json()) as unknown;
  const parsed = CreateBriefShareResponseSchema.safeParse(raw);
  if (!parsed.success) {
    logger.warn('createBriefShare response failed schema', {
      briefId,
      error: parsed.error.format(),
    });
    throw new Error('create_brief_share_invalid_response');
  }
  return parsed.data;
}

/**
 * Revoke a share token. Server returns 204/200 on success — either way
 * the Firestore snapshot picks up the deletion and the UI removes the
 * row.
 */
export async function deleteBriefShare(
  briefId: string,
  token: string,
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/briefs/${encodeURIComponent(briefId)}/share/${encodeURIComponent(token)}`,
    {
      method: 'DELETE',
      headers: await authHeaders(),
    },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    logger.warn('deleteBriefShare failed', {
      briefId,
      status: res.status,
      body: text.slice(0, 200),
    });
    throw new Error(`${res.status} ${text}`);
  }
}

/**
 * Schema-check + preview an upload before kicking off a real ingest.
 * `sourceFormat` defaults to `'canonical-corpus'` for back-compat —
 * pass `'fec-contributions'` / `'irs990-officers'` / `'irs990-grants'`
 * with the matching raw payload (parsed JSON or CSV string) to validate
 * via the server's adapter.
 */
export async function validateIngest(
  corpus: unknown,
  fileHash?: string,
  sourceFormat: SourceFormat = 'canonical-corpus',
): Promise<ValidateIngestResponse> {
  const res = await fetch(`${API_BASE}/admin/ingest/validate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await authHeaders()),
    },
    body: JSON.stringify({
      corpus,
      sourceFormat,
      ...(fileHash ? { fileHash } : {}),
    }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    logger.warn('validateIngest failed', {
      status: res.status,
      body: text.slice(0, 200),
    });
    throw new Error(`${res.status} ${text}`);
  }
  const raw = (await res.json()) as unknown;
  const parsed = ValidateIngestResponseSchema.safeParse(raw);
  if (!parsed.success) {
    logger.warn('validateIngest response failed schema', {
      error: parsed.error.format(),
    });
    throw new Error('validate_ingest_invalid_response');
  }
  return parsed.data;
}

/**
 * Preview the diff between an uploaded corpus and the current graph.
 * Read-only — runs two batched UNWIND queries against `:Entity` indexes.
 * Returns counts + samples so the validated-state UI can render
 * "X new, Y will upsert" alongside the standard preview.
 */
export async function previewIngestDiff(
  corpus: Corpus,
): Promise<IngestDiffStats> {
  const res = await fetch(`${API_BASE}/admin/ingest/preview-diff`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await authHeaders()),
    },
    body: JSON.stringify({ corpus }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    logger.warn('previewIngestDiff failed', {
      status: res.status,
      body: text.slice(0, 200),
    });
    throw new Error(`${res.status} ${text}`);
  }
  const raw = (await res.json()) as unknown;
  const parsed = PreviewDiffResponseSchema.safeParse(raw);
  if (!parsed.success) {
    logger.warn('previewIngestDiff response failed schema', {
      error: parsed.error.format(),
    });
    throw new Error('preview_diff_invalid_response');
  }
  return parsed.data;
}

/**
 * Cancel an in-flight ingest. Returns once the cancel signal is recorded
 * (Firestore + same-instance abort). The server still owes a final SSE
 * frame on the run stream — the streaming side observes the cancel flag
 * between documents and emits a `canceled` event before closing.
 */
export async function cancelIngestRun(runId: string): Promise<void> {
  const res = await fetch(
    `${API_BASE}/admin/ingest/runs/${encodeURIComponent(runId)}/cancel`,
    {
      method: 'POST',
      headers: await authHeaders(),
    },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${res.status} ${text}`);
  }
}

/**
 * Roll back a finished run by deleting every node + edge stamped with
 * its `ingest_run_id`. Refuses while still running. Returns the deletion
 * counts so the UI can render "removed N nodes / M edges" feedback.
 */
export async function rollbackIngestRunById(
  runId: string,
): Promise<RollbackIngestResponse> {
  const res = await fetch(
    `${API_BASE}/admin/ingest/runs/${encodeURIComponent(runId)}/rollback`,
    {
      method: 'POST',
      headers: await authHeaders(),
    },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${res.status} ${text}`);
  }
  const raw = (await res.json()) as unknown;
  const parsed = RollbackIngestResponseSchema.safeParse(raw);
  if (!parsed.success) {
    logger.warn('rollbackIngestRun response failed schema', {
      runId,
      error: parsed.error.format(),
    });
    throw new Error('rollback_ingest_invalid_response');
  }
  return parsed.data;
}

/**
 * List recent ingest runs, newest-first. Server caps at 200; default 50.
 */
export async function listIngestRuns(limit = 50): Promise<IngestRun[]> {
  const raw = await getJson<unknown>(`/admin/ingest/runs?limit=${limit}`);
  const parsed = ListIngestRunsResponseSchema.safeParse(raw);
  if (!parsed.success) {
    logger.warn('listIngestRuns response failed schema', {
      error: parsed.error.format(),
    });
    throw new Error('list_ingest_runs_invalid_response');
  }
  return parsed.data.runs;
}

/**
 * Active org's ingest history, newest-first. Server caps at 100; default 25.
 * Server gates on `orgMember` and pulls runs where `orgId == activeOrgId`.
 */
export async function listOrgIngestHistory(limit = 25): Promise<IngestRun[]> {
  const raw = await getJson<unknown>(`/org/ingest/history?limit=${limit}`);
  const parsed = ListIngestRunsResponseSchema.safeParse(raw);
  if (!parsed.success) {
    logger.warn('listOrgIngestHistory response failed schema', {
      error: parsed.error.format(),
    });
    throw new Error('list_org_ingest_history_invalid_response');
  }
  return parsed.data.runs;
}

/**
 * Re-run a previous org ingest with the same data the server stored at
 * submit time. Creates a new `ingestRuns/{id}` record on the server and
 * streams progress over SSE — exactly the same wire format as
 * {@link startOrgIngest}, just sourced from the persisted payload
 * instead of an upload. The first SSE event is `run_started` carrying
 * the new runId so the caller can update its UI to follow the new run.
 */
export function retryOrgIngestRun(
  sourceRunId: string,
  onEvent: (event: IngestSseEvent) => void,
  onError?: (err: Error) => void,
): () => void {
  const controller = new AbortController();
  (async () => {
    try {
      const res = await fetch(
        `${API_BASE}/org/ingest/runs/${encodeURIComponent(sourceRunId)}/retry`,
        {
          method: 'POST',
          headers: {
            Accept: 'text/event-stream',
            ...(await authHeaders()),
          },
          signal: controller.signal,
        },
      );
      if (!res.ok || !res.body) {
        const text = await res.text().catch(() => '');
        let code: string | undefined;
        let detail: unknown;
        try {
          const parsed = JSON.parse(text) as {
            error?: string;
            detail?: unknown;
          };
          code = parsed.error;
          detail = parsed.detail;
        } catch {
          /* non-JSON */
        }
        throw new IngestStartError(
          code ?? `${res.status}`,
          text || `${res.status}`,
          detail,
          res.status,
        );
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let boundary = buffer.indexOf('\n\n');
        while (boundary !== -1) {
          const frame = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);
          const dataLine = frame
            .split('\n')
            .find((l) => l.startsWith('data: '));
          if (dataLine) {
            try {
              const payload = JSON.parse(dataLine.slice(6)) as unknown;
              const parsed = IngestSseEventSchema.safeParse(payload);
              if (parsed.success) {
                onEvent(parsed.data);
              } else {
                logger.warn('retryOrgIngestRun received unparseable frame', {
                  error: parsed.error.format(),
                });
              }
            } catch {
              /* ignore malformed frame */
            }
          }
          boundary = buffer.indexOf('\n\n');
        }
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') onError?.(err as Error);
    }
  })();
  return () => controller.abort();
}

/**
 * Single org-scoped ingest run, including the full `errors[]` array.
 * Server 404s if the runId belongs to a different org.
 */
export async function getOrgIngestRun(runId: string): Promise<IngestRun> {
  const raw = await getJson<unknown>(
    `/org/ingest/runs/${encodeURIComponent(runId)}`,
  );
  const parsed = (raw && typeof raw === 'object' && 'run' in raw
    ? IngestRunSchema.safeParse((raw as { run: unknown }).run)
    : { success: false as const, error: { format: () => 'no run field' } });
  if (!parsed.success) {
    logger.warn('getOrgIngestRun response failed schema', {
      runId,
      error: 'error' in parsed ? parsed.error.format() : 'invalid wrapper',
    });
    throw new Error('get_org_ingest_run_invalid_response');
  }
  return parsed.data;
}

/** Read a single ingest run by id, including its full error log. */
export async function getIngestRun(runId: string): Promise<IngestRun> {
  const raw = await getJson<unknown>(
    `/admin/ingest/runs/${encodeURIComponent(runId)}`,
  );
  const parsed = IngestRunSchema.safeParse(raw);
  if (!parsed.success) {
    logger.warn('getIngestRun response failed schema', {
      runId,
      error: parsed.error.format(),
    });
    throw new Error('get_ingest_run_invalid_response');
  }
  return parsed.data;
}

/**
 * Thrown when `POST /admin/ingest/run` returns a non-2xx status. Carries
 * the server's `error` code + `detail` object so the UI can render a
 * friendly banner (e.g. "another ingest is already running — started by X
 * at Y") instead of a raw status-line dump.
 *
 * `code` is widened to `IngestErrorCode | string` because the server may
 * also return generic transport errors (e.g. an HTTP-level "503") that
 * aren't in our typed enum. Branching code in the UI compares against
 * literal members of `IngestErrorCode`; everything else falls to default.
 */
export class IngestStartError extends Error {
  readonly code: IngestErrorCode | string;
  readonly detail: unknown;
  readonly status: number;
  constructor(
    code: IngestErrorCode | string,
    message: string,
    detail: unknown,
    status: number,
  ) {
    super(message);
    this.name = 'IngestStartError';
    this.code = code;
    this.detail = detail;
    this.status = status;
  }
}

/**
 * Kick off an ingest and consume its SSE stream. Mirrors {@link askStream}
 * exactly: same fetch shape, same `data: {...}\n\n` boundary parser.
 * Each frame is parsed with the IngestSseEvent zod schema and any frame
 * that fails parsing is dropped (log + continue) so a single malformed
 * payload can't kill the run UI.
 *
 * Returns an abort function that detaches the client from the SSE stream
 * but does NOT cancel the server-side run. To stop the run on the server,
 * call {@link cancelIngestRun} — that signals via Firestore + the
 * in-process registry, and the run finalizes with `status: 'canceled'`.
 * Whether you abort or not, the persisted `ingestRuns/{id}` doc gets a
 * final write.
 */
export function startIngest(
  body: StartIngestRequest,
  onEvent: (event: IngestSseEvent) => void,
  onError?: (err: Error) => void,
): () => void {
  const controller = new AbortController();
  (async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/ingest/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...(await authHeaders()),
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      if (!res.ok || !res.body) {
        // Structured error surface for the UI — 409 + 413 have meaningful
        // detail payloads the caller needs to render as a friendly banner
        // rather than as a raw "409 {...}" blob.
        const text = await res.text().catch(() => '');
        let code: string | undefined;
        let detail: unknown;
        try {
          const parsed = JSON.parse(text) as {
            error?: string;
            detail?: unknown;
          };
          code = parsed.error;
          detail = parsed.detail;
        } catch {
          /* non-JSON — fall through to a plain Error */
        }
        throw new IngestStartError(
          code ?? `${res.status}`,
          text || `${res.status}`,
          detail,
          res.status,
        );
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let boundary = buffer.indexOf('\n\n');
        while (boundary !== -1) {
          const frame = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);
          const dataLine = frame
            .split('\n')
            .find((l) => l.startsWith('data: '));
          if (dataLine) {
            try {
              const payload = JSON.parse(dataLine.slice(6)) as unknown;
              const parsed = IngestSseEventSchema.safeParse(payload);
              if (parsed.success) {
                onEvent(parsed.data);
              } else {
                logger.warn('startIngest received unparseable frame', {
                  error: parsed.error.format(),
                });
              }
            } catch {
              /* ignore malformed frame */
            }
          }
          boundary = buffer.indexOf('\n\n');
        }
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') onError?.(err as Error);
    }
  })();
  return () => controller.abort();
}

/**
 * Org-admin variant of {@link startIngest} that hits `/api/org/ingest`.
 * Same wire format (StartIngestRequest in, IngestSseEvent stream out)
 * but the server scopes every Document/edge/Candidate to the X-Org-Id
 * org instead of stamping system data. Mode is forced to `merge`
 * server-side; org imports never wipe.
 */
export function startOrgIngest(
  body: StartIngestRequest,
  onEvent: (event: IngestSseEvent) => void,
  onError?: (err: Error) => void,
): () => void {
  const controller = new AbortController();
  (async () => {
    try {
      const res = await fetch(`${API_BASE}/org/ingest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...(await authHeaders()),
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      if (!res.ok || !res.body) {
        const text = await res.text().catch(() => '');
        let code: string | undefined;
        let detail: unknown;
        try {
          const parsed = JSON.parse(text) as {
            error?: string;
            detail?: unknown;
          };
          code = parsed.error;
          detail = parsed.detail;
        } catch {
          /* non-JSON */
        }
        throw new IngestStartError(
          code ?? `${res.status}`,
          text || `${res.status}`,
          detail,
          res.status,
        );
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let boundary = buffer.indexOf('\n\n');
        while (boundary !== -1) {
          const frame = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);
          const dataLine = frame
            .split('\n')
            .find((l) => l.startsWith('data: '));
          if (dataLine) {
            try {
              const payload = JSON.parse(dataLine.slice(6)) as unknown;
              const parsed = IngestSseEventSchema.safeParse(payload);
              if (parsed.success) {
                onEvent(parsed.data);
              } else {
                logger.warn('startOrgIngest received unparseable frame', {
                  error: parsed.error.format(),
                });
              }
            } catch {
              /* ignore malformed frame */
            }
          }
          boundary = buffer.indexOf('\n\n');
        }
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') onError?.(err as Error);
    }
  })();
  return () => controller.abort();
}

export interface AskStreamOptions {
  /**
   * Conversation thread id. When set, the server reads prior turns from
   * Firestore and pre-pends them to the model's `messages` array as
   * native multi-turn context.
   */
  threadId?: string;
  /**
   * Surface subject the chat is anchored to (entity dossier, graph view,
   * etc.). The server folds a one-line "Surface context" block into the
   * prompt so unqualified pronouns resolve correctly.
   */
  subject?: ThreadSubject;
  /**
   * Transient view state — filters/hides/pins/selection — sent on the
   * request only. Lets the assistant reason about the same subgraph
   * the analyst is looking at without persisting that state to the
   * thread doc.
   */
  surfaceState?: SurfaceState;
}

/**
 * Consume /api/ask as a server-sent event stream.
 * Parses each `data: { ... }` frame as an AskSseEvent and calls `onEvent`.
 * Returns a cancellation function.
 */
export function askStream(
  question: string,
  onEvent: (event: AskSseEvent) => void,
  onError?: (err: Error) => void,
  options: AskStreamOptions = {},
): () => void {
  const controller = new AbortController();
  (async () => {
    try {
      const res = await fetch(`${API_BASE}/ask`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...(await authHeaders()),
        },
        body: JSON.stringify({
          question,
          ...(options.threadId ? { threadId: options.threadId } : {}),
          ...(options.subject ? { subject: options.subject } : {}),
          ...(options.surfaceState
            ? { surfaceState: options.surfaceState }
            : {}),
        }),
        signal: controller.signal,
      });
      if (!res.ok || !res.body) {
        throw new Error(`${res.status} ${await res.text().catch(() => '')}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let boundary = buffer.indexOf('\n\n');
        while (boundary !== -1) {
          const frame = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);
          const dataLine = frame.split('\n').find((l) => l.startsWith('data: '));
          if (dataLine) {
            try {
              const payload = JSON.parse(dataLine.slice(6)) as AskSseEvent;
              onEvent(payload);
            } catch {
              /* ignore malformed frame */
            }
          }
          boundary = buffer.indexOf('\n\n');
        }
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') onError?.(err as Error);
    }
  })();
  return () => controller.abort();
}
